import i18n from "i18next";
import { useFlags } from "launchdarkly-react-client-sdk";

import { useAttributesContext } from "shared/contexts/AttributesContext";
import { ContextProviderName } from "shared/contexts/types";
import {
  GENERIC_FILTER,
  REPAIRS_GENERIC_FILTER,
  SIGNAL_EVENTS_GENERIC_FILTER,
  WORK_ORDERS_GENERIC_FILTER,
} from "shared/filterDefinitions";
import { VEHICLE_ACCESSOR } from "shared/schemas/constants";

import { FilterType } from "features/ui/Filters/types";

import usePartSchema from "./partSchema";
import useRepairLaborSchema from "./repairLaborSchema";
import useRepairPartsSchema from "./repairPartsSchema";
import useServiceCentersSchema from "./serviceCentersSchema";
import { SchemaEntryOverride, UseSchema } from "./types";
import { getGroupBySelectOptions } from "./utils/group-by-utils";
import {
  createMinimalSchema,
  getAttributeMap,
  getDisplayLabelFunction,
  getFilterFunction,
  getSchemaEntryFunction,
  getSchemaForAccessorFunction,
  getSchemaFromAttributes,
  sortAndFilterSchemaByWhitelistedAccessors,
} from "./utils/schema-utils";
import useVehiclesSchema from "./vehiclesSchema";
import useWorkOrderPartsSchema from "./workOrderPartsSchema";

interface RepairsSchemaProps {
  accessorsToHide?: string[];
  orderedWhitelistedAccessors?: string[];
  includeWorkOrdersRelation?: boolean;
}

const useRepairsSchema = ({
  accessorsToHide = [],
  orderedWhitelistedAccessors,
  includeWorkOrdersRelation = true,
}: RepairsSchemaProps = {}): UseSchema => {
  const t = i18n.t;
  const { warrantyClaimsCost: warrantyClaimsCostFF } = useFlags();

  const { attributes: repairAttributes, groupByValues } = useAttributesContext(
    ContextProviderName.Repair
  );
  const repairAttributeMap = getAttributeMap(repairAttributes);
  const getRepairDisplayLabel = getDisplayLabelFunction(repairAttributeMap);
  const getRepairFilter = getFilterFunction(
    REPAIRS_GENERIC_FILTER,
    repairAttributeMap
  );
  const getRepairSchemaEntry = getSchemaEntryFunction(
    getRepairFilter,
    repairAttributeMap
  );

  const repairOverrides: SchemaEntryOverride = {
    [VEHICLE_ACCESSOR]: {
      schemaOverride: {
        filter: undefined,
      },
    },
    costLabor: { ignore: !warrantyClaimsCostFF },
    costParts: { ignore: !warrantyClaimsCostFF },
    costOther: { ignore: !warrantyClaimsCostFF },
    relatedSignalEventOccurrences: {
      filterOverride: SIGNAL_EVENTS_GENERIC_FILTER({
        fieldName: "relatedSignalEventOccurrences",
        fieldNameForAPI: "ID",
        label: getRepairDisplayLabel(
          "relatedSignalEventOccurrences",
          t("common:columns.related_signal_events")
        ),
        search: true,
        disableFiltering: true,
        disableContainsFilters: true,
        filterType: FilterType.OCCURS,
        baseEntityText: t("common:event_type.repair_one"),
      }),
    },
  };

  const partsSchema = usePartSchema();
  const vehiclesSchema = useVehiclesSchema();

  const { attributes: workOrderAttributes } = useAttributesContext(
    ContextProviderName.WorkOrder
  );
  const workOrderPartsSchema = useWorkOrderPartsSchema();
  const serviceCentersSchema = useServiceCentersSchema([
    "createdAt",
    "updatedAt",
  ]);

  const repairRelations: Record<string, UseSchema> = {
    "/api/v1/parts": partsSchema,
  };

  if (includeWorkOrdersRelation) {
    // Create a minimal work orders schema without the repairs relationship
    const workOrdersSchemaForRepairs = createMinimalSchema(
      workOrderAttributes,
      WORK_ORDERS_GENERIC_FILTER,
      {},
      {
        "/api/v1/workOrderParts": workOrderPartsSchema,
        "/api/v1/serviceCenters": serviceCentersSchema,
      }
    );

    repairRelations["/api/v1/workOrders"] = workOrdersSchemaForRepairs;
  }

  const repairSchema = getSchemaFromAttributes(
    repairAttributes,
    getRepairSchemaEntry,
    repairOverrides,
    repairRelations,
    accessorsToHide
  );

  const groupByRelations = {
    "/api/v1/vehicles": vehiclesSchema,
  };

  const attributesForGroupBy = repairAttributes?.filter(
    (attr) => !accessorsToHide.includes(attr.ID)
  );

  const groupBySelectOptions = getGroupBySelectOptions(
    attributesForGroupBy,
    groupByRelations,
    groupByValues
  );

  const repairsUseSchema: UseSchema = {
    schema: sortAndFilterSchemaByWhitelistedAccessors(
      repairSchema,
      orderedWhitelistedAccessors
    ),
    getDisplayLabel: getRepairDisplayLabel,
    attributeMap: repairAttributeMap,
    attributes: repairAttributes,
    attributeAccessors: repairSchema.map(
      ({ accessor, filter }) => filter?.fieldName || accessor
    ),
    getSchemaEntry: getRepairSchemaEntry,
    getSchemaForAccessor: getSchemaForAccessorFunction(repairSchema),
    groupBySelectOptions,
  };

  return repairsUseSchema;
};

const V2_VALUES_ENDPOINT = "v2/workOrders/repairs";

const useRepairsSchemaV2 = (
  accessorsToHide: string[] = [],
  includeWorkOrdersRelation = true
): UseSchema => {
  const { attributes } = useAttributesContext(ContextProviderName.RepairV2);
  const attributeMap = getAttributeMap(attributes);
  const getDisplayLabel = getDisplayLabelFunction(attributeMap);
  const getFilter = getFilterFunction(
    GENERIC_FILTER,
    attributeMap,
    V2_VALUES_ENDPOINT
  );
  const getSchemaEntry = getSchemaEntryFunction(getFilter, attributeMap);

  const partsSchema = useRepairPartsSchema();
  const laborSchema = useRepairLaborSchema();

  const repairRelations: Record<string, UseSchema> = {
    "/api/v2/repairParts": partsSchema,
    "/api/v2/repairLabor": laborSchema,
  };

  const { attributes: workOrderAttributes } = useAttributesContext(
    ContextProviderName.WorkOrderV2
  );

  if (includeWorkOrdersRelation) {
    // Create a minimal work orders schema without the repairs relationship
    const workOrdersSchemaForRepairs = createMinimalSchema(
      workOrderAttributes,
      GENERIC_FILTER,
      {}
    );

    repairRelations["/api/v2/workOrders"] = workOrdersSchemaForRepairs;
  }

  const schema = getSchemaFromAttributes(
    attributes,
    getSchemaEntry,
    undefined,
    repairRelations,
    accessorsToHide
  );

  return {
    schema,
    getDisplayLabel,
    attributeMap,
    attributes,
    attributeAccessors: schema.map(
      ({ accessor, filter }) => filter?.fieldName || accessor
    ),
    getSchemaEntry,
    getSchemaForAccessor: getSchemaForAccessorFunction(schema),
  };
};

export { useRepairsSchemaV2 };

export default useRepairsSchema;
